import type { BlobRecord, BlobStorage } from '../storage';
import { SingletonLocker } from '../storage/lock';
import type { BlobSync } from '../sync/blob';

export class BlobFrontend {
  // Since 'set' and 'get' operations may be called in rapid succession, we use a lock mechanism
  // to ensure that 'get' requests for the same blob are paused when a 'set' operation is in progress.
  private readonly lock = new SingletonLocker();
  private readonly downloadingQueue = new Map<string, Promise<BlobRecord | null>>();
  private activeDownloads = 0;
  private readonly MAX_CONCURRENT = 8;  // 匹配浏览器限制
  private readonly waitingQueue: Array<() => void> = [];

  constructor(
    readonly storage: BlobStorage,
    private readonly sync: BlobSync
  ) {}

  get state$() {
    return this.sync.state$;
  }

  blobState$(blobId: string) {
    return this.sync.blobState$(blobId);
  }

  async get(blobId: string) {
    // ✅ 优化 1：先尝试直接从本地获取
    try {
      const local = await this.storage.get(blobId);
      if (local) {
        return local;  // 本地有就秒返
      }
    } catch (err) {
      console.warn('Direct local get failed:', err);
    }

    // ✅ 优化 2：本地没有才等待连接（加超时）
    try {
      await Promise.race([
        this.waitForConnected(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('IDB timeout')), 5000)
        )
      ]);
    } catch (err) {
      console.warn('Wait for connection failed:', err);
    }

    // 双重检查（可能在等待期间已被下载）
    await using lock = await this.lock.lock('blob', blobId);
    const localAgain = await this.storage.get(blobId);
    if (localAgain) {
      return localAgain;
    }
    await lock[Symbol.asyncDispose]();

    // ✅ 检查是否已在下载
    const existing = this.downloadingQueue.get(blobId);
    if (existing) return await existing;

    // ✅ 等待并发槽位
    await this.acquireSlot();

    const promise = (async () => {
      try {
        await this.sync.downloadBlob(blobId).catch(() => {});
        return await this.storage.get(blobId);
      } finally {
        this.releaseSlot();
        this.downloadingQueue.delete(blobId);
      }
    })();

    this.downloadingQueue.set(blobId, promise);
    return await promise;
  }

  async set(blob: BlobRecord) {
    await this.waitForConnected();
    await using lock = await this.lock.lock('blob', blob.key);
    await this.storage.set(blob);
    await lock[Symbol.asyncDispose]();

    // We don't wait for the upload to complete,
    // as the upload process runs asynchronously in the background
    this.sync.uploadBlob(blob, true /* force upload */).catch(() => {
      // ignore the error as it has already been recorded in the sync status
    });

    return;
  }

  /**
   * Uploads a blob to the peer. Do nothing if the blob has already been uploaded.
   *
   * @returns Always resolves to true when successful
   *
   * @throws This method will throw an error if the blob is not found locally, if the upload is aborted, or if it fails due to storage limitations.
   */
  async upload(blobIdOrRecord: string | BlobRecord): Promise<boolean> {
    await this.waitForConnected();
    const blob =
      typeof blobIdOrRecord === 'string'
        ? await this.storage.get(blobIdOrRecord)
        : blobIdOrRecord;
    if (!blob) {
      throw new Error(`Blob ${blobIdOrRecord} not found`);
    }
    return this.sync.uploadBlob(blob, false);
  }

  fullDownload(peerId?: string, signal?: AbortSignal) {
    return this.sync.fullDownload(peerId, signal);
  }

  private waitForConnected(signal?: AbortSignal) {
    return this.storage.connection.waitForConnected(signal);
  }

  private async acquireSlot() {
    if (this.activeDownloads < this.MAX_CONCURRENT) {
      this.activeDownloads++;
      return;
    }
    return new Promise<void>(resolve => {
      this.waitingQueue.push(resolve);
    });
  }

  private releaseSlot() {
    this.activeDownloads--;
    const next = this.waitingQueue.shift();
    if (next) {
      this.activeDownloads++;
      next();
    }
  }
}
